import {
  format,
  isAfter,
  isEqual,
  isSameDay,
  isSameYear,
  isValid,
  parse,
  subDays,
} from "date-fns";
import { fi } from "date-fns/locale";
import { Optional } from "../types/nullable";
import getLocale from "./getLocale";

type Operation = "dateTimeRange" | "dateTimeRangeLabels";
type TimeRangeFormat = "short" | "long";

type FormatDateTimeRelativeToNowOptions = {
  now?: Date;
  formats?: Partial<typeof defaultDateTimeRelativeToNowFormats>;
  labels?: {
    today?: string;
    yesterday?: string;
  };
};

export const shortDateFormat = "d.M.yyyy";
export const shortDayMonthFormat = "d.M.";
export const leadingZerosDayMonthFormat = "dd.MM.";
export const leadingZerosDateFormat = "dd.MM.yyyy";
export const leadingZerosWithDayOfWeekFormat = "EEEEEE dd.MM.yyyy";
export const dateTimeFormat = "dd.MM.yyyy HH:mm";
export const dateTimeSecondsFormat = "dd.MM.yyyy HH:mm:ss";
export const isoFormat = "yyyy-MM-dd";
export const dateMonthYearFormat = "dd-MM-yyyy";
export const yearMonthFormat = "yyyy-MM";
export const shortMonthYearFormat = "MM/yy";
export const hoursMinutesFormat = "HH:mm";
export const rawHoursMinutesFormat = "HHmm";
export const shortDayMonthTimeFormat = `${shortDayMonthFormat} ${hoursMinutesFormat}`;
export const shortDateTimeFormat = `${shortDateFormat} ${hoursMinutesFormat}`;

const TIME_PATTERN = /^\d{2}:\d{2}$/;

export const combineDateAndTime = (
  date: Date | null,
  time: string,
): Date | null => {
  if (!date || !isValid(date) || !TIME_PATTERN.test(time)) return null;

  const [hours, minutes] = time.split(":").map(Number);
  const combined = new Date(date);
  combined.setHours(hours, minutes, 0, 0);
  return combined;
};

export const isUntilAfterFrom = (
  from: Date | null,
  until: Date | null,
): boolean => {
  if (!from || !until) return true;

  return isAfter(until, from);
};

export const dateTimeFormatLong = {
  format: "EEEEEE dd.MM. 'klo' HH:mm",
  locale: {
    locale: fi,
  },
};

/**
 * Formats date to ISO format
 *
 * @param date - The date string
 * @param from - The format of the date string
 */
export const toIsoDateString = (date: string, from: string): string =>
  format(parse(date, from, new Date()), isoFormat);

/**
 * Formats date from ISO format
 *
 * @param date - The date string in ISO format
 * @param to - The desired format
 */
export const fromIsoDateString = (date: Optional<string>, to: string) => {
  if (!date) return "";

  const parsed = parse(date, isoFormat, new Date());
  return format(parsed, to);
};

/** Parses a yyyy-MM-dd string as a local date. Impossible calendar days return null. */
export const parseIsoDate = (value: unknown): Date | null => {
  if (typeof value !== "string" || !value) return null;
  const date = parse(value, isoFormat, new Date());
  if (!isValid(date) || format(date, isoFormat) !== value) return null;
  return date;
};

/**
 * Formats today's date to desired format
 *
 * @param inFormat - The desired format
 */
export const today = (inFormat: string): string => format(new Date(), inFormat);

export const getStartTime = (time: string) => time.slice(0, 4);
export const getEndTime = (time: string) => time.slice(4);

const getFormattedTime = (time: string): string => {
  const hour = time.slice(0, 2);
  const minute = time.slice(2);
  return `${hour}:${minute}`;
};

const getWeekday = (date: Date, weekdayFormat: string): string => {
  const locale = getLocale();
  const weekday = format(date, weekdayFormat || "EEEEEE", { locale });
  const firstLetter = weekday.charAt(0);
  const firstLetterCap = firstLetter.toUpperCase();
  const remainingLetters = weekday.slice(1);
  return `${firstLetterCap}${remainingLetters}`;
};

export const isValidTime = (time: string) =>
  !time || time.length !== 8 ? "" : time;

/**
 * Validates a date object for user input fields
 * Checks if date is valid, doesn't contain leading zeros in year, and optionally validates year range
 *
 * @param date - The date to validate (null is considered valid for optional fields)
 * @param validateYearRange - Whether to validate year is between 1970-2099 (default: false)
 * @returns true if date is valid or null, false otherwise
 */
export const validateDateInput = (
  date: Date | null,
  validateYearRange = false,
): boolean => {
  if (date === null) return true; // null is valid (optional field)
  if (!isValid(date)) return false;

  const formattedDate = format(date, "yyyy-MM-dd");
  const dateParts = formattedDate.split("-");
  const yearContainsLeadingZeros = /^0+/.test(dateParts?.[0]);

  if (!dateParts || formattedDate.length !== 10 || yearContainsLeadingZeros) {
    return false;
  }

  // Optional year range validation (matching DateRangePicker minDate/maxDate when used)
  if (validateYearRange) {
    const year = date.getFullYear();
    return year >= 1970 && year <= 2099;
  }

  return true;
};

const getFormattedDate = (
  date: Date | null,
  labels: boolean,
  formats: { [key: string]: any } = { date: "", weekday: "" },
): string => {
  if (!date || !isValid(date)) return "";
  const weekday = labels ? getWeekday(date, formats?.weekday) : null;
  const dateString = format(date, formats?.date || "d.M.yyyy");
  return labels ? `${weekday} ${dateString}` : `${dateString}`;
};

// 08:00 - 16:00 or Klo 08:00 - 16:00
const formatTimeRange = (
  time: string,
  format: TimeRangeFormat = "short",
): string => {
  if (!isValidTime(time)) return "";

  const startTime = getFormattedTime(getStartTime(time));
  const endTime = getFormattedTime(getEndTime(time));

  return format === "short"
    ? `${startTime} - ${endTime}`
    : `Klo ${startTime} - ${endTime}`;
};

const formatDateTimeRange = (
  date: Date,
  timeRange: string,
  labels: boolean,
  formatString?: string,
): string => {
  const formattedDate = getFormattedDate(date, labels, { date: formatString });

  if (!isValidTime(timeRange)) return "";

  return labels
    ? `${formattedDate} ${formatTimeRange(timeRange, "long")}`
    : `${formattedDate} ${formatTimeRange(timeRange)}`;
};

const formatDateRange = (
  dateRange: Array<Date> | null,
  formatString?: string,
) => {
  if (!dateRange) return "";

  const getTime = (date: Date) => date.getTime();

  const minDate = new Date(Math.min(...dateRange.map(getTime)));
  const maxDate = new Date(Math.max(...dateRange.map(getTime)));
  const isEqualDates = isEqual(minDate, maxDate);

  /**
   * for now, only the format of the first date can be customized
   * e.g., d.M. - d.m.yyyy
   */
  return isEqualDates
    ? getFormattedDate(minDate, true, { date: formatString })
    : `${getFormattedDate(minDate, false, {})} - ${getFormattedDate(
        maxDate,
        false,
      )}`;
};

/**
 * Formats a date range with month/year aware compact rules.
 *
 * Examples:
 * - Same month + year: 1.–3.2.
 * - Different month, same year: 28.2.–2.3.
 * - Different year: 28.12.2026–3.1.2027
 */
export const formatDateRangeMonthYearAware = (
  startDay: Date,
  endDayInclusive: Date,
  showYear = false,
): string => {
  if (!isValid(startDay) || !isValid(endDayInclusive)) return "";

  const start =
    startDay.getTime() <= endDayInclusive.getTime()
      ? startDay
      : endDayInclusive;
  const end =
    startDay.getTime() <= endDayInclusive.getTime()
      ? endDayInclusive
      : startDay;

  const d1 = start.getDate();
  const m1 = start.getMonth() + 1;
  const y1 = start.getFullYear();

  const d2 = end.getDate();
  const m2 = end.getMonth() + 1;
  const y2 = end.getFullYear();

  // Different year: include year on both ends
  if (y1 !== y2) {
    return `${d1}.${m1}.${y1} - ${d2}.${m2}.${y2}`;
  }

  // Same year, different month: show day.month on both ends
  if (m1 !== m2) {
    return `${d1}.${m1}. - ${d2}.${m2}.${showYear ? y2 : ""}`;
  }

  // Same year + same month: show month once at the end
  return `${d1}. - ${d2}.${m2}.${showYear ? y2 : ""}`;
};

const formatDateTime = (
  date: Date | null,
  time: string,
  operation: Operation,
  formatString?: string,
) => {
  if (!date) return "";

  switch (operation) {
    case "dateTimeRange":
      return formatDateTimeRange(date, time, false, formatString);
    case "dateTimeRangeLabels":
      return formatDateTimeRange(date, time, true, formatString);
    default:
      return "";
  }
};

const defaultDateTimeRelativeToNowFormats = {
  today: hoursMinutesFormat,
  sameYear: shortDayMonthTimeFormat,
  otherYear: shortDateTimeFormat,
};

/**
 * Formats a timestamp relative to now.
 * Defaults: today 14:32, same year 3.10. 14:32, other year 3.10.2025 14:32.
 * With labels: today "Tänään 14:32", yesterday "Eilen 14:32".
 */
export const formatDateTimeRelativeToNow = (
  value: Date | string | number,
  options: FormatDateTimeRelativeToNowOptions = {},
): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (!isValid(date)) return "";

  const now = options.now ?? new Date();
  const formats = {
    ...defaultDateTimeRelativeToNowFormats,
    ...options.formats,
  };
  const time = format(date, hoursMinutesFormat);
  const { today: todayLabel, yesterday: yesterdayLabel } = options.labels ?? {};

  if (isSameDay(date, now)) {
    return todayLabel ? `${todayLabel} ${time}` : format(date, formats.today);
  }
  if (yesterdayLabel && isSameDay(date, subDays(now, 1))) {
    return `${yesterdayLabel} ${time}`;
  }
  if (isSameYear(date, now)) return format(date, formats.sameYear);
  return format(date, formats.otherYear);
};

export const formatLeadingZerosDateWithDayOfWeek = (date: Date): string => {
  if (!isValid(date)) return "";
  return format(date, leadingZerosWithDayOfWeekFormat, { locale: getLocale() });
};

export { formatDateRange, formatDateTime, formatTimeRange, getFormattedDate };
